import { filter, type Observable } from 'rxjs';
import { DeclarativeRouter } from './declarativeRouter';
import type { WithPrefix } from './filterRouteActive';
import { routes, type RouteTree } from './globalRoutes';
import {
  type LocationIn,
  type MatchedLocationIn,
  type NotFoundLocation,
  type RouteIn,
  type UnmatchedLocation,
} from './location';
import type { RouteNode } from './routeNode';

export const location$ = new DeclarativeRouter(routes);

/**
 * A place that can be navigated to in ardoq-front.
 */
export type AppLocation<Prefix extends AppRoute | '/' = '/'> = WithPrefix<
  LocationIn<RouteTree>,
  Prefix
>;

export type AppRoute = RouteIn<RouteTree>;

/**
 * A location `location$` emits: a place in the app, the not found location
 * produced for a partial match, or an unmatched location when HMR removes the
 * active route.
 *
 * The content of the prefix that did match renders around the not found page, so
 * it sees the not found location too. `filterRouteActive` narrows this back to
 * {@link AppLocation}.
 */
export type AppRouterLocation =
  | MatchedLocationIn<RouteTree>
  | NotFoundLocation
  | UnmatchedLocation;

const isAppLocation = (
  location: AppRouterLocation
): location is MatchedLocationIn<RouteTree> => location.kind === 'matched';

/**
 * `location$`, narrowed to the locations that name a route.
 *
 * For code that acts on where the user is — navigating relative to it, writing
 * it back to the URL — rather than rendering at it. A not found location names no
 * route, so there is nothing for that code to act on.
 */
export const matchedLocation$: Observable<AppLocation> = location$.pipe(
  filter(isAppLocation)
);

export const prefetchRoutes = (route: RouteNode = routes) => {
  // Force invocation of `import(...)` to prefetch the route
  // Skip await so the browser can load at its own pace
  route.content?.();
  for (const child of route.children ?? []) {
    prefetchRoutes(child);
  }
};

if (import.meta.webpackHot) {
  import.meta.webpackHot.accept('./globalRoutes', () => {
    location$.hmr(routes);
  });
}

if (import.meta.hot) {
  import.meta.hot.accept('./globalRoutes', newModule => {
    if (newModule) {
      location$.hmr(newModule.routes);
    }
  });
}
